<template>
  <div
    v-if="totalItems > rowsPerPage"
    class="pagination-wrapper"
  >
    <div class="p-paginator">
      <button
        class="p-paginator-first p-paginator-element p-link"
        :disabled="currentPage === 1"
        @click="$emit('update:currentPage', 1)"
      >
        <span class="p-paginator-icon pi pi-angle-double-left" />
      </button>
      <button
        class="p-paginator-prev p-paginator-element p-link"
        :disabled="currentPage === 1"
        @click="$emit('update:currentPage', currentPage - 1)"
      >
        <span class="p-paginator-icon pi pi-angle-left" />
      </button>
      <span class="p-paginator-pages">
        <button
          v-for="page in totalPages"
          :key="page"
          class="p-paginator-page p-paginator-element p-link"
          :class="{ 'p-highlight': page === currentPage }"
          @click="$emit('update:currentPage', page)"
        >
          {{ page }}
        </button>
      </span>
      <button
        class="p-paginator-next p-paginator-element p-link"
        :disabled="currentPage === totalPages"
        @click="$emit('update:currentPage', currentPage + 1)"
      >
        <span class="p-paginator-icon pi pi-angle-right" />
      </button>
      <button
        class="p-paginator-last p-paginator-element p-link"
        :disabled="currentPage === totalPages"
        @click="$emit('update:currentPage', totalPages)"
      >
        <span class="p-paginator-icon pi pi-angle-double-right" />
      </button>
      <span class="p-paginator-current">
        {{ t('results') }} {{ firstItem }} {{ t('to') }} {{ lastItem }} {{ t('of') }} {{ totalItems }}
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'IntegrationPagination',
  props: {
    currentPage: {
      type: Number,
      required: true,
    },

    totalItems: {
      type: Number,
      required: true,
    },

    rowsPerPage: {
      type: Number,
      required: true,
    },
  },

  emits: ['update:currentPage'],

  setup(props) {
    const { t } = useI18n();

    const totalPages = computed(() => Math.ceil(props.totalItems / props.rowsPerPage));

    const firstItem = computed(() => {
      if (props.totalItems === 0) return 0;
      return (props.currentPage - 1) * props.rowsPerPage + 1;
    });

    const lastItem = computed(() => {
      const last = props.currentPage * props.rowsPerPage;
      return last > props.totalItems ? props.totalItems : last;
    });

    return {
      t,
      totalPages,
      firstItem,
      lastItem,
    };
  },
});
</script>

<style scoped lang="scss">
.pagination-wrapper {
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
}

.p-paginator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.p-paginator-element {
  min-width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  padding: 0 0.75rem;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #94c840;
    color: #94c840;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.p-highlight {
    background: #94c840;
    border-color: #94c840;
    color: #ffffff;
    font-weight: 600;
  }
}

.p-paginator-pages {
  display: flex;
  gap: 0.25rem;
}

.p-paginator-current {
  margin-left: 1rem;
  color: #6b7280;
  font-size: 0.875rem;
  white-space: nowrap;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    text-align: center;
    margin-top: 0.5rem;
  }
}

.p-paginator-icon {
  font-size: 0.875rem;
}
</style>
